import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STORAGE_KEY = 'dws_user';

export const setAxiosAuthToken = (token) => {
  if (token) axios.defaults.headers.common.Authorization = `Bearer ${token}`;
  else delete axios.defaults.headers.common.Authorization;
};

export const readStoredUser = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const writeStoredUser = (user) => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(user));
};

// Access tokens expire every ~15 minutes. A single in-flight refresh is shared by
// every request that 401s in the meantime, so a burst of parallel calls produces
// one refresh instead of N competing ones (which would rotate the refresh token
// repeatedly and trip the reuse detector).
let refreshPromise = null;

const performRefresh = async () => {
  const stored = readStoredUser();
  const refreshToken = stored?.refresh_token || stored?.refreshToken;
  if (!refreshToken) throw new Error('No refresh token available');

  // Bare axios instance: must not inherit the Authorization header or this
  // interceptor, otherwise a failing refresh would recurse.
  const response = await axios.create().post(`${API}/auth/refresh`, {
    refresh_token: refreshToken
  });

  const data = response.data || {};
  const updated = {
    ...stored,
    ...data,
    token: data.token,
    refresh_token: data.refresh_token || data.refreshToken,
    firebase_token: data.firebase_token || data.firebaseToken
  };

  writeStoredUser(updated);
  setAxiosAuthToken(updated.token);
  return updated;
};

export const refreshAccessToken = () => {
  if (!refreshPromise) {
    refreshPromise = performRefresh().finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
};

/**
 * Installs a response interceptor that retries a request once after refreshing
 * an expired access token. `onRefreshFailed` is called when the refresh itself
 * is rejected, which means the session is genuinely gone.
 */
export const installAuthInterceptor = ({ onRefreshed, onRefreshFailed } = {}) => {
  return axios.interceptors.response.use(
    (response) => response,
    async (error) => {
      const original = error.config;
      const status = error.response?.status;

      // Only retry a genuine 401, and only once per request.
      if (status !== 401 || !original || original._retriedAfterRefresh) {
        return Promise.reject(error);
      }

      // Guest users without a refresh token cannot refresh.
      const stored = readStoredUser();
      const hasRefreshToken = Boolean(stored?.refresh_token || stored?.refreshToken);
      if (stored?.is_guest && !hasRefreshToken) {
        return Promise.reject(error);
      }

      // Never try to refresh the auth endpoints themselves.
      if (original.url && /\/auth\/(login|signup|refresh)/.test(original.url)) {
        return Promise.reject(error);
      }

      original._retriedAfterRefresh = true;

      try {
        const updated = await refreshAccessToken();
        if (onRefreshed) onRefreshed(updated);
        original.headers = { ...original.headers, Authorization: `Bearer ${updated.token}` };
        return axios(original);
      } catch (refreshError) {
        if (onRefreshFailed) onRefreshFailed(refreshError);
        return Promise.reject(error);
      }
    }
  );
};
